import React, { useEffect, useMemo } from "react";
import { Link, Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import PageTransition from "./PageTransition";
import {
  LayoutDashboard,
  Users,
  Building2,
  GraduationCap,
  Menu,
  X,
  Search,
  ChevronDown,
  Upload,
  ShieldCheck,
  UserCog,
  BarChart3,
  LogOut,
} from "lucide-react";
import ScopeFilter from "@/components/ScopeFilter";
import { ScopeProvider } from "@/context/ScopeContext";
import CommandPalette from "@/components/CommandPalette";
import ThemeToggle from "@/components/ThemeToggle";
import "./DashboardTopBar.css";
import "./DashboardSidebar.css";

const AdvancedAnalyticsIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m3 3 7.5 7.5" />
    <path d="M21 3 10.5 13.5" />
    <path d="M21 21 13.5 13.5" />
    <path d="M3 21l10.5-10.5" />
  </svg>
);

const EXECUTIVE_TAB_LABELS = {
  overview: "Overview",
  map: "Map View",
  identity: "Student Overview",
  infrastructure: "Infrastructure Overview",
  teacher: "Teacher Overview",
  operational: "Operational Overview",
};

const ANALYTICS_TAB_LABELS = {
  map: "Block Map",
  insights: "Executive Insights",
  executive: "Executive Insights",
  dropout: "Dropout Risk",
  infrastructure: "Infrastructure",
  teacher: "Teacher Forecast",
  completion: "Data Completion",
};

const navSections = [
  {
    id: "analytics",
    title: "Analytics",
    items: [
      { type: "link", path: "/executive-dashboard", icon: BarChart3, label: "Executive Dashboard" },
      { type: "link", path: "/analytics-dashboard", icon: AdvancedAnalyticsIcon, label: "Advanced Analytics" },
      { type: "link", path: "/state-overview", icon: LayoutDashboard, label: "State Overview", exact: true },
    ],
  },
  {
    id: "school-analytics-section",
    title: "School Analytics",
    items: [
      {
        type: "group",
        id: "school-analytics",
        icon: Building2,
        label: "School Analytics",
        children: [
          { path: "/school-type-bifurcation", label: "Enrollment Analytics" },
          { path: "/classrooms-toilets-dashboard", label: "Classroom & Toilet Dashboard" },
          { path: "/infrastructure-dashboard", label: "Water Safety Dashboard" },
          { path: "/data-entry-dashboard", label: "Data Entry Status Dashboard" },
        ],
      },
    ],
  },
  {
    id: "student-section",
    title: "Student",
    items: [
      {
        type: "group",
        id: "student",
        icon: ShieldCheck,
        label: "Student Analytics",
        children: [
          { path: "/aadhaar-analytics", label: "Aadhaar Analytics Dashboard" },
          { path: "/enrolment-dashboard", label: "Enrolment Analytics Dashboard" },
          { path: "/dropbox-dashboard", label: "Dropbox Analytics Dashboard" },
          { path: "/age-enrolment-dashboard", label: "Agewise Enrolment Dashboard" },
          { path: "/apaar-dashboard", label: "Apaar Analytics Dashboard" },
        ],
      },
    ],
  },
  {
    id: "teachers-section",
    title: "Teachers",
    items: [
      {
        type: "group",
        id: "teachers",
        icon: Users,
        label: "Teachers Analytics",
        children: [
          { path: "/teacher-dashboard", label: "Teachers Dashboard" },
          { path: "/ctteacher-dashboard", label: "CTeachers Analytics" },
        ],
      },
    ],
  },
];

const adminSection = {
  id: "admin-section",
  title: "Admin",
  items: [
    { type: "link", path: "/data-import", icon: Upload, label: "Data Import", adminOnly: true },
    { type: "link", path: "/user-management", icon: UserCog, label: "User Management", adminOnly: true },
  ],
};

const childIcons = {
  "/classrooms-toilets-dashboard": Building2,
  "/infrastructure-dashboard": Building2,
  "/data-entry-dashboard": Building2,
  "/school-type-bifurcation": Building2,
  "/aadhaar-analytics": ShieldCheck,
  "/enrolment-dashboard": ShieldCheck,
  "/dropbox-dashboard": ShieldCheck,
  "/age-enrolment-dashboard": ShieldCheck,
  "/apaar-dashboard": ShieldCheck,
  "/teacher-dashboard": Users,
  "/ctteacher-dashboard": Users,
};

const flattenNavItems = (sections) => {
  const result = [];
  for (const section of sections) {
    for (const item of section.items) {
      if (item.type === "group") {
        item.children.forEach((child) => {
          result.push({
            path: child.path,
            label: child.label,
            icon: childIcons[child.path] || item.icon,
          });
        });
      } else if (item.type === "link" && !item.adminOnly) {
        result.push(item);
      }
    }
  }
  return result;
};

const findNavItemByPath = (pathname) => {
  const allSections = [...navSections, adminSection];
  for (const section of allSections) {
    for (const item of section.items) {
      if (item.type === "link" && item.path === pathname) return item;
      if (item.type === "group") {
        const child = item.children.find((c) => c.path === pathname);
        if (child) return { ...child, icon: childIcons[child.path] || item.icon };
      }
    }
  }
  return null;
};

const getGroupIdForPath = (pathname) => {
  for (const section of navSections) {
    for (const item of section.items) {
      if (item.type === "group" && item.children.some((c) => c.path === pathname)) {
        return item.id;
      }
    }
  }
  return null;
};

const getUserInitials = (fullName) => {
  if (!fullName) return "U";
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return fullName.slice(0, 2).toUpperCase();
};

const formatRole = (role) => {
  if (!role) return "Viewer";
  if (role === "admin") return "Administrator";
  return role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const DashboardLayout = ({ user, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [openGroups, setOpenGroups] = React.useState({});
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const activeGroupId = getGroupIdForPath(location.pathname);
    if (activeGroupId) {
      setOpenGroups((prev) => ({ ...prev, [activeGroupId]: true }));
    }
  }, [location.pathname]);

  const toggleGroup = (groupId) => {
    setOpenGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const visibleSections = useMemo(() => {
    const sections = [...navSections];
    const adminItems = adminSection.items.filter((item) => !item.adminOnly || user?.role === "admin");
    if (adminItems.length) {
      sections.push({ ...adminSection, items: adminItems });
    }
    return sections;
  }, [user?.role]);

  const flattenedNavItems = useMemo(() => flattenNavItems(visibleSections), [visibleSections]);

  const handleLogout = () => {
    if (onLogout) onLogout();
    navigate("/");
  };

  const getBreadcrumbs = () => {
    const paths = location.pathname.split("/").filter(Boolean);
    const searchParams = new URLSearchParams(location.search);
    const execTab = paths[0] === "executive-dashboard" ? searchParams.get("tab") : null;
    const analyticsTab = paths[0] === "analytics-dashboard"
      ? (searchParams.get("analyticsTab") || searchParams.get("tab"))
      : null;
    const breadcrumbs = [{ label: "Maharashtra", path: "/state-overview", tone: "muted" }];

    if (paths.length === 0 || paths[0] === "state-overview") {
      breadcrumbs.push({ label: "State Overview", path: "/state-overview", tone: "current" });
    } else if (paths[0] === "district") {
      breadcrumbs.push({ label: "Districts", path: "/state-overview", tone: "muted" });
      breadcrumbs.push({ label: paths[1], path: `/district/${paths[1]}`, tone: "current" });
    } else if (paths[0] === "block") {
      breadcrumbs.push({ label: "Blocks", path: "/state-overview", tone: "muted" });
      breadcrumbs.push({ label: paths[1], path: `/block/${paths[1]}`, tone: "current" });
    } else if (paths[0] === "school") {
      breadcrumbs.push({ label: "Schools", path: "/state-overview", tone: "muted" });
      breadcrumbs.push({ label: paths[1], path: `/school/${paths[1]}`, tone: "current" });
    } else if (paths[0] === "executive-dashboard") {
      breadcrumbs.push({
        label: "Executive Dashboard",
        path: "/executive-dashboard",
        tone: execTab && execTab !== "overview" ? "active" : "active",
      });
      if (execTab && execTab !== "overview" && EXECUTIVE_TAB_LABELS[execTab]) {
        breadcrumbs.push({ label: EXECUTIVE_TAB_LABELS[execTab], path: null, tone: "current" });
      }
    } else if (paths[0] === "analytics-dashboard") {
      breadcrumbs.push({
        label: "Advanced Analytics",
        path: "/analytics-dashboard",
        tone: analyticsTab && analyticsTab !== "map" ? "active" : "active",
      });
      const analyticsTabLabel = analyticsTab === "executive" ? "insights" : analyticsTab;
      if (analyticsTabLabel && analyticsTabLabel !== "map" && ANALYTICS_TAB_LABELS[analyticsTabLabel]) {
        breadcrumbs.push({ label: ANALYTICS_TAB_LABELS[analyticsTabLabel], path: null, tone: "current" });
      }
    } else {
      const item = findNavItemByPath(`/${paths[0]}`);
      if (item) {
        breadcrumbs.push({ label: item.label, path: item.path, tone: "current" });
      }
    }

    return breadcrumbs;
  };

  const closeSidebarOnNavigate = () => {
    if (typeof window !== "undefined" && window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  const renderNavItem = (item) => {
    if (item.type === "link") {
      const Icon = item.icon;
      return (
        <NavLink
          key={item.path}
          to={item.path}
          end={item.exact}
          onClick={closeSidebarOnNavigate}
          className={({ isActive }) => `dl-menu-item${isActive ? " active" : ""}`}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
        >
          <Icon className="w-[19px] h-[19px]" strokeWidth={1.75} />
          <span>{item.label}</span>
        </NavLink>
      );
    }

    const Icon = item.icon;
    const isOpen = !!openGroups[item.id];
    const hasActiveChild = item.children.some((child) => location.pathname === child.path);
    const groupClass = ["dl-menu-item dl-menu-group-trigger", hasActiveChild ? "group-active" : ""]
      .filter(Boolean)
      .join(" ");

    return (
      <div key={item.id} className="dl-menu-group">
        <button
          type="button"
          className={groupClass}
          onClick={() => toggleGroup(item.id)}
          aria-expanded={isOpen}
          aria-controls={`submenu-${item.id}`}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
        >
          <Icon className="w-[19px] h-[19px]" strokeWidth={1.75} />
          <span className="flex-1 text-left">{item.label}</span>
          <ChevronDown className={`dl-chevron${isOpen ? " open" : ""}`} strokeWidth={2} />
        </button>
        {isOpen && (
          <div className="dl-submenu" id={`submenu-${item.id}`}>
            {item.children.map((child) => (
              <NavLink
                key={`${item.id}-${child.path}`}
                to={child.path}
                onClick={closeSidebarOnNavigate}
                className={({ isActive }) => (isActive ? "active" : undefined)}
                data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {child.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <ScopeProvider>
      <div className="dashboard-layout">
        <aside className={`dl-sidebar ${sidebarOpen ? "open" : ""}`} aria-label="Main sidebar menu">
          <div className="dl-sidebar-brand">
            <div className="dl-sidebar-brand-main">
              <span className="dl-brand-icon" aria-hidden="true">
                <GraduationCap className="w-[30px] h-[30px]" strokeWidth={1.75} />
              </span>
              <span className="dl-brand-copy">
                <strong>MH Education</strong>
                <span>Dashboard 2025–26</span>
              </span>
            </div>
            <button
              type="button"
              className="dl-sidebar-close lg:hidden"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="dl-sidebar-scroll">
            {visibleSections.map((section) => (
              <section key={section.id} className="dl-sidebar-section">
                <div className="dl-sidebar-title">{section.title}</div>
                <nav className="dl-menu">{section.items.map((item) => renderNavItem(item))}</nav>
              </section>
            ))}
          </div>

          <div className="dl-sidebar-footer">
            <div className="dl-user-card">
              <span className="dl-avatar">{getUserInitials(user?.full_name)}</span>
              <span className="min-w-0 flex-1">
                <b>{user?.full_name || "System Admin"}</b>
                <span>{formatRole(user?.role)}</span>
              </span>
              <button
                type="button"
                onClick={handleLogout}
                className="dl-sidebar-close shrink-0"
                title="Logout"
                aria-label="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </aside>

        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <main className="main-content relative">
          <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_20%_10%,hsl(var(--accent))_0%,transparent_45%),radial-gradient(circle_at_80%_0%,hsl(var(--chart-3))_0%,transparent_40%)] opacity-[0.10]" />
          <header className="dl-topbar" aria-label="Dashboard top bar">
            <div className="dl-topbar-inner">
              <div className="dl-topbar-start">
                <button
                  type="button"
                  className="dl-mobile-menu-btn lg:hidden"
                  onClick={() => setSidebarOpen(true)}
                  data-testid="mobile-menu-btn"
                  aria-label="Open menu"
                >
                  <Menu className="w-5 h-5" />
                </button>

                <nav className="dl-breadcrumb" aria-label="Breadcrumb">
                  {getBreadcrumbs().map((crumb, idx, arr) => (
                    <React.Fragment key={`${crumb.label}-${idx}`}>
                      {crumb.path ? (
                        <Link
                          to={crumb.path}
                          className={crumb.tone === "active" ? "dl-crumb-active" : undefined}
                        >
                          {crumb.label}
                        </Link>
                      ) : (
                        <span className="dl-crumb-current">{crumb.label}</span>
                      )}
                      {idx < arr.length - 1 && <span className="dl-sep">›</span>}
                    </React.Fragment>
                  ))}
                </nav>
              </div>

              <div className="dl-top-actions" aria-label="Global controls">
                <ScopeFilter variant="chip" />
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new Event("open-command-palette"))}
                  className="dl-search-box"
                  data-testid="search-input"
                  title="Search dashboards"
                >
                  <Search className="h-4 w-4" />
                  <span>Search dashboards...</span>
                </button>
                <ThemeToggle />
              </div>
            </div>
          </header>

          <div className="p-4 lg:p-8">
            <PageTransition>
              <Outlet />
            </PageTransition>
          </div>
        </main>
        <CommandPalette navItems={flattenedNavItems} onLogout={handleLogout} />
      </div>
    </ScopeProvider>
  );
};

export default DashboardLayout;
